import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { LocalStorageEnum } from 'src/app/shared/enum.ts/local-storage.enum';
import { IUserToken } from 'src/app/shared/models/local-storage';
import { ApiHttpService } from './api-http.service';
import { LocalStorageService } from './local-storage.service';

export interface LoginRequest {
  email: string;
  password: string;
  fcmToken?: string;
}

export interface LoginResponse {
  status: string;
  access_token: string;
  refresh_token: string;
  user: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(ApiHttpService);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);

  login(body: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginRequest, LoginResponse>('auth/login', body, {
      withCredentials: true,
    });
  }

  /**
   * Fires the logout request and returns the observable. Callers subscribe
   * with `takeUntilDestroyed` and call `clearSession()` on next/error so the
   * UI side-effect (storage wipe + redirect) lives in the caller's lifecycle.
   * Per implementation-guidelines §17.1 (services own URL+verb+types only).
   */
  logout(): Observable<unknown> {
    return this.http.post<Record<string, never>, unknown>('auth/logout', {});
  }

  /**
   * Clears stored credentials and bounces to the login page. Optionally
   * preserves the URL the user was on so the login flow can return them
   * after a successful sign-in. Used by the 401 branch in `AuthInterceptor`
   * and by the menu's sign-out command.
   */
  clearSession(returnUrl?: string): void {
    localStorage.clear();
    this.router.navigate(['/auth/login'], returnUrl ? { queryParams: { returnUrl } } : undefined);
  }

  setAuthToken(token: string): void {
    this.localStorageService.setItem(LocalStorageEnum.ACCESS_TOKEN, { access_token: token });
  }

  getAuthToken(): IUserToken {
    return this.localStorageService.getItem(LocalStorageEnum.ACCESS_TOKEN);
  }
}
